import React, { useState } from 'react';
import { Grid, Box, Pagination, AmountSelector, PageSelector, NextPreviousBtn, Icon, Modal, Button, Typography } from '@blueprint/ui';
import { Table, TableBodyCell, TableHeadCell, TableBodyRow, TableHeadRow, TableHead, TableBody, TSortLabel } from '@blueprint/table';
import { useTable, usePagination, useSortBy } from 'react-table';
import { axiosInstance } from '../../functions/axios';
import { useTranslation } from 'react-i18next';

const AmountComponent = ({ children, count, prefixText: PrefixText, suffixText: SuffixText }) => (
    <>
        <PrefixText>Show</PrefixText>
        {children}
        <SuffixText>of {count} accounts</SuffixText>
    </>
);

const amountOptions = [
    { label: '5', value: '5' },
    { label: '10', value: '10' },
    { label: '20', value: '20' },
    { label: '50', value: '50' },
];

const AccountHistoryTableComponent = ({ data, columns, id, fetchAccountsAgain }) => {
    const { t } = useTranslation();
    const {
        previousPage,
        nextPage,
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        setPageSize,
        gotoPage,
        state: { pageIndex, pageSize },
    } = useTable({ data, columns, ...{ disableSortRemove: true } }, useSortBy, usePagination);

    const [open, setOpen] = useState(false);
    const [accountId, setAccountId] = useState(0);

    const DELETE_BANK_ACCOUNT = process.env.REACT_APP_DELETE_BANK_ACCOUNT;
    const deleteAccount = (accountId) => {
        const deleteAccountApiCall = async () => {
            try {
                const response = await axiosInstance.post(`${DELETE_BANK_ACCOUNT}/${accountId}`);
                if (response.status === 200) {
                    console.log('Account deleted successfully', response.data);
                    fetchAccountsAgain();
                }
            } catch (error) {
                console.log('Logging DELETE_ACCOUNT_URL error', error);
            }
        };
        deleteAccountApiCall();
    };

    return (
        <>
            <Grid container style={{ overflowX: 'auto', msOverflowStyle: 'none' }}>
                <Table {...getTableProps()} tableHover width='99%' id={id}>
                    <TableHead>
                        {headerGroups.map((headerGroup) => (
                            <TableHeadRow {...headerGroup.getHeaderGroupProps()}>
                                <TableHeadCell>Action</TableHeadCell>
                                {headerGroup.headers.map((column) => {
                                    return (
                                        <TableHeadCell
                                            cellType='sortable'
                                            {...column.getHeaderProps(column.getSortByToggleProps())}
                                            isSorted={column.isSorted}
                                            aria-sort={column.canSort ? (column.isSortedDesc ? 'descending' : 'ascending') : undefined}
                                            onKeyPress={(event) => {
                                                if (event.charCode === 13) {
                                                    column.toggleSortBy();
                                                }
                                            }}
                                        >
                                            {column.render('Header')}
                                            <TSortLabel
                                                aria-label={'Sort ' + `${column.Header}`}
                                                sortType={column.canSort ? (column.isSortedDesc ? 'descending' : 'ascending') : undefined}
                                            />
                                        </TableHeadCell>
                                    );
                                })}
                            </TableHeadRow>
                        ))}
                    </TableHead>
                    <TableBody {...getTableBodyProps()}>
                        {rows.map((row, i) => {
                            // Ignore this warning or just return something to suppress it
                            if (
                                // This "if" ensures that records are displayed according to 'Page Size' and 'Selected Page'
                                pageIndex * pageSize <= i &&
                                i < (pageIndex + 1) * pageSize
                            ) {
                                prepareRow(row);

                                return (
                                    <TableBodyRow {...row.getRowProps()}>
                                        <TableBodyCell
                                            onClick={() => {
                                                console.log('Logging entire row', row);
                                            }}
                                        >
                                            <Icon
                                                name='Delete'
                                                id={`download_${row.id}`}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => {
                                                    console.log('Trying to delete Account with ID:', row.original.accountId);
                                                    setAccountId(row.original.accountId);
                                                    setOpen(true);
                                                    //deleteAccount(row.original.id);
                                                }}
                                            />
                                        </TableBodyCell>
                                        {row.cells.map((cell, index) => {
                                            return (
                                                <TableBodyCell
                                                    onClick={() => {
                                                        console.log('Logging Cell', cell.row.original);
                                                    }}
                                                    {...cell.getCellProps()}
                                                    id={`row${row.id}col${index}`}
                                                    textAlign={typeof cell.value === 'number' ? 'right' : 'left'}
                                                    style={{ height: '-webkit-fill-available' }}
                                                >
                                                    {cell.render('Cell')}
                                                </TableBodyCell>
                                            );
                                        })}
                                    </TableBodyRow>
                                );
                            }
                        })}
                    </TableBody>
                </Table>
            </Grid>
            {data.length > 10 ? (
                <Grid container>
                    <Pagination
                        count={data.length}
                        defaultCountPerPage={10}
                        defaultCurrentPage={1}
                        mt='20px'
                        mb='15px'
                        width='100%'
                        display='flex'
                        flexWrap='wrap'
                    >
                        <AmountSelector
                            id='pl-dropdown-pageSize'
                            amountComponent={AmountComponent}
                            options={amountOptions}
                            onAmountChange={(countPerPage) => {
                                setPageSize(countPerPage);
                                gotoPage(0);
                            }}
                        />
                        <Box display='flex'>
                            <PageSelector
                                id='pl-dropdown-pageSelector'
                                isSearchable={true}
                                onPageChange={(currentPage, event, { countPerPage }) => {
                                    gotoPage(currentPage - 1);
                                }}
                            />
                            <NextPreviousBtn
                                id='pl-buttons-nextPrevious'
                                previousOnClick={(newCurrentPage, count, { countPerPage }) => {
                                    previousPage();
                                }}
                                nextOnClick={(newCurrentPage, count, { countPerPage }) => {
                                    nextPage();
                                }}
                            />
                        </Box>
                    </Pagination>
                </Grid>
            ) : null}

            <Modal
                open={open}
                onHide={() => setOpen(false)}
                closeIcon={true}
                onClickCloseIcon={() => setOpen(false)}
                size='small'
                activeFocusTrap={true}
                aria-labelledby='title1'
                minHeight='230px'
            >
                <Box display='flex' justifyContent='center' width='100%' marginTop='10%'>
                    <Box>
                        <Typography variant='h4' id='paymentsList-typo-modal-text' fontWeight='bold'>
                            {t('accountHistory.bankAccDeletion')}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    position='absolute'
                    bottom='0'
                    py='1em'
                    width='calc(100% - 80px)'
                    display='flex'
                    justifyContent='space-between'
                    borderTop='1px solid #333'
                >
                    <Button variant='light.tertiary' onClick={() => setOpen(false)} id='paymentsList-button-modal-no'>
                        {t('userTable.no')}
                    </Button>
                    <Button
                        onClick={() => {
                            deleteAccount(accountId);
                            setOpen(false);
                        }}
                        id='paymentsList-button-modal-yes'
                    >
                        {t('userTable.yes')}
                    </Button>
                </Box>
            </Modal>
        </>
    );
};

export default AccountHistoryTableComponent;
